import { useLayoutEffect, useCallback, type RefObject } from "react";

interface UseFitTextOptions {
  minFontSize?: number;
}

export function useFitText(
  ref: RefObject<HTMLElement>,
  dependencies: any[] = [],
  options: UseFitTextOptions = {}
) {
  const { minFontSize = 8 } = options;

  const adjust = useCallback(() => {
    const element = ref.current;
    if (!element || !element.parentElement) return;

    const container = element.parentElement;
    const computedStyle = window.getComputedStyle(container);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    const paddingRight = parseFloat(computedStyle.paddingRight);

    // Reset font size to original to get initial font size from CSS
    element.style.fontSize = "";
    const initialFontSize = parseFloat(
      window.getComputedStyle(element).fontSize
    );

    element.style.fontSize = `${initialFontSize}px`;

    if (initialFontSize <= minFontSize) {
      return;
    }

    const availableHeight = container.clientHeight - paddingTop - paddingBottom;
    const availableWidth = container.clientWidth - paddingLeft - paddingRight;

    const isOverflowing = () =>
      element.scrollHeight > availableHeight ||
      element.scrollWidth > availableWidth;

    if (!isOverflowing()) {
      // It fits, so we ensure it's at the initial size.
      element.style.fontSize = `${initialFontSize}px`;
      return;
    }

    let currentFontSize = initialFontSize;

    // Iteratively decrease font size
    while (isOverflowing() && currentFontSize > minFontSize) {
      currentFontSize--;
      element.style.fontSize = `${currentFontSize}px`;
    }

    // Final check
    if (isOverflowing()) {
      element.style.fontSize = `${minFontSize}px`;
    }
  }, [ref, minFontSize]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !element.parentElement) return;

    adjust();

    const observer = new ResizeObserver(adjust);
    observer.observe(element.parentElement);
    observer.observe(element);

    return () => {
      if (element.parentElement) {
        observer.unobserve(element.parentElement);
      }
      observer.unobserve(element);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjust, ...dependencies]);
}
